import React, { useState, useRef, useEffect, useContext } from 'react';
import _ from 'lodash';
import { Accordion, Card, Button, Spinner } from 'react-bootstrap';
import { ChevronLeft, ChevronDown } from 'react-bootstrap-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleRight } from '@fortawesome/free-solid-svg-icons';

import AppContext from '../../../context';
import { updateExistingTxs, fetchCompanySummary } from '../../../../api';
import { Number } from '../../common/common';
import EditTxsTable from './edit_txs_table.component';
import './view_edit_txs.component.css';

const ViewEditTxs = (props) => {
  const context = useContext(AppContext);
  const { pfId, setStep, onSave } = props;

  //state
  const [currentStep, setCurrentStep] = useState('one');
  const [summary, setSummary] = useState(props.summary);
  //edit txs
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAllUpdated, setIsAllUpdated] = useState(false);
  const editedTxsRef = useRef({});
  //temp summary ref while saving in progress
  const tempSummaryRef = useRef(props.summary);
  const tempUpdatedCountRef = useRef({ success: 0, error: 0 });
  //current view company ref
  const currentCompanyRef = useRef(null);

  const getEditedStatusTag = (status) => {
    let tag = '';
    switch (status) {
      case 'edited':
        tag = <span className="edited_status__tag edited">Edited</span>;
        break;

      case 'saved':
        tag = <span className="edited_status__tag saved">Saved</span>;
        break;

      default:
        tag = <span className="edited_status__tag error">{status}</span>;
        break;
    }
    return tag;
  };

  const updateTx = async (fincode, payload) => {
    try {
      const res = await updateExistingTxs(pfId, [payload]);

      const cSummary = _.cloneDeep(tempSummaryRef.current);
      const instIdx = _.findIndex(cSummary, { fincode: parseInt(fincode) });
      cSummary[instIdx].edited_status = 'saved';
      tempSummaryRef.current = cSummary;
      tempUpdatedCountRef.current.success += 1;
    } catch (error) {
      const errorMsg =
        error.response && error.response.status === 400
          ? 'Invalid transactions'
          : 'Something went wrong';
      const cSummary = _.cloneDeep(tempSummaryRef.current);
      const instIdx = _.findIndex(cSummary, { fincode: parseInt(fincode) });
      cSummary[instIdx].edited_status = errorMsg;
      tempSummaryRef.current = cSummary;
      tempUpdatedCountRef.current.error += 1;
    }
  };

  const updateTxs = async () => {
    setIsUpdating(true);
    const promises = [];
    _.forOwn(editedTxsRef.current, (value, key) => {
      const promise = updateTx(key, value);
      promises.push(promise);
    });
    Promise.all(promises).then(() => {
      setIsAllUpdated(true);
    });
  };

  useEffect(() => {
    if (isAllUpdated) {
      setSummary(tempSummaryRef.current);
      setIsUpdating(false);
    }
  }, [isAllUpdated]);

  //android dep start
  const getSummary = async () => {
    try {
      const res = await fetchCompanySummary(pfId);
      setSummary(res.data.data.company_summary.active);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!props.summary) getSummary();
  }, [props.summary]);
  //android dep end

  return (
    summary && (
      <>
        <div id="calendar-portal"></div>
        <div className="add-tx-content px-md-5 pt-md-5 px-sm-4 pt-sm-4 px-4 pt-4">
          <div className="add-tx-header">
            <span className="step-text">
              {context.portfoliosById[pfId].name}
            </span>
            <h5 className="step-heading pt-1">
              View/Edit transactions{' '}
              {currentStep === 'two'
                ? `- ${currentCompanyRef.current.name}`
                : ''}
            </h5>
            <span className="step-info">
              Data includes all corporate actions such as bonus & split amounts
              into the portfolio
            </span>
          </div>
          <div
            id="transactions"
            className="add-tx- mt-3 position-relative flex-grow"
          >
            <div className="h-100 overflow-auto">
              {currentStep === 'one' &&
                summary &&
                summary.map((ctxs, idx_comp) => (
                  <div
                    key={ctxs.fincode}
                    className="ctxs_card"
                    onClick={() => {
                      currentCompanyRef.current = ctxs;
                      setCurrentStep('two');
                    }}
                  >
                    <b>{ctxs.name}</b>
                    <div className="d-flex align-items-center">
                      {ctxs.edited_status &&
                        getEditedStatusTag(ctxs.edited_status)}
                      <span className="total_cost pr-3">
                        Total Cost:{' '}
                        <span style={{ fontWeight: 500 }}>
                          <Number
                            value={Math.abs(ctxs.total_cost)}
                            usePrefix={true}
                          />
                        </span>
                      </span>
                      <FontAwesomeIcon icon={faArrowCircleRight} />
                    </div>
                  </div>
                ))}
              {currentStep === 'two' && (
                <EditTxsTable
                  pfId={pfId}
                  fincode={currentCompanyRef.current.fincode}
                  txs={currentCompanyRef.current.txs}
                  setEditedTxsRef={(fincode, value) => {
                    const cSummary = _.cloneDeep(summary);
                    const instIdx = _.findIndex(cSummary, {
                      fincode,
                    });
                    cSummary[instIdx].edited_status = 'edited';
                    editedTxsRef.current[fincode] = value;
                    setSaveDisabled(false);
                    setSummary(cSummary);
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="mr-auto back-button btn btn-sm"
            onClick={() => {
              if (props.isWebview) {
                window.Android.goBack();
                return;
              }
              if (currentStep === 'two') {
                setCurrentStep('one');
                return;
              }
              setStep('manage_two');
            }}
          >
            <ChevronLeft /> <span>back</span>
          </button>
          {currentStep === 'one' && (
            <>
              <span className="footer-text">
                Click on save to apply the changes to your portfolio
              </span>
              <Button
                size="sm"
                onClick={() => {
                  isAllUpdated ? onSave() : updateTxs();
                }}
                variant="success"
                className={`modal-btn save ${isAllUpdated ? 'close-btn' : ''}`}
                disabled={saveDisabled || isUpdating}
              >
                {isAllUpdated ? (
                  'Close'
                ) : isUpdating ? (
                  <>
                    <Spinner animation="border" variant="light" size="sm" />{' '}
                    Saving
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </>
          )}
        </div>
      </>
    )
  );
};

export default ViewEditTxs;
